import moment from 'moment';
import * as yup from 'yup';
import URI from 'urijs';
import { omit } from 'lodash';
import { momentToApiDate } from 'Utils/date/apiDate';
import { graphqlifyString } from 'Utils/graphql';
import graphQLClient from 'Utils/graphql-client/graphqlClient';
import { WIDGET_QUOTATION_FORM_SUBMIT } from 'Utils/request/requestDictionary';
import { capitalizeFromUppercase } from 'Utils/string/stringUtils';
import { GM_NUMBER_PATTERN } from 'Components/utils/common-validator/CommonValidators';
import {
  MIN_FORM_ADULTS,
  MIN_FORM_KIDS,
  MIN_FORM_TEENS,
  NO_VALUE,
} from './productQuotationForm.constants';
export type FormFieldOption = {
  name: string;
  value: string;
};
export type FormTraveler = {
  civility: string;
  email: string;
  firstName: string;
  lastName: string;
  phone: string;
  gmNumber: string;
  agency: string;
};
type FormDepartureDate =
  | string
  | {
      from: null | Date;
      to: null;
    };
export type FormValues = {
  adultsNumber: string;
  comment: string;
  departureCity: string;
  departureDate: FormDepartureDate;
  extension?: string;
  kids: Array<{
    birthdate: null | timestamp;
  }>;
  kidsNumber: string;
  optinEmail: boolean;
  traveler: FormTraveler;
};
// Initialize form values
type InitialFormValuesParameters = {
  departureCityDisabled: boolean;
  extensionOptions: ReadonlyArray<FormFieldOption>;
  isProductWithSetDates: boolean;
  isSummerCamp: boolean;
  traveler: Partial<FormTraveler>;
};
export const getInitialFormValues = ({
  departureCityDisabled,
  extensionOptions,
  isProductWithSetDates,
  isSummerCamp,
  traveler,
}: InitialFormValuesParameters): FormValues => {
  return {
    adultsNumber: isSummerCamp ? MIN_FORM_TEENS.toString() : MIN_FORM_ADULTS.toString(),
    comment: '',
    departureCity: departureCityDisabled ? NO_VALUE : '',
    departureDate: !isProductWithSetDates
      ? {
          from: null,
          to: null,
        }
      : '',
    ...(extensionOptions.length > 0
      ? {
          extension: '',
        }
      : null),
    ...(isSummerCamp
      ? null
      : {
          kidsNumber: MIN_FORM_KIDS.toString(),
        }),
    optinEmail: false,
    traveler,
  };
};
// Set form schema
type FormSchemaParameters = {
  agencies: Array<FormFieldOption>;
  civilityOptions: ReadonlyArray<FormFieldOption>;
  departureCityOptions: ReadonlyArray<FormFieldOption>;
  departureDateOptions: ReadonlyArray<FormFieldOption>;
  extensionOptions: ReadonlyArray<FormFieldOption>;
  isProductWithSetDates: boolean;
  isSummerCamp: boolean;
};
export const getFormSchema = (
  t: (arg0: string) => string,
  {
    agencies,
    civilityOptions,
    departureCityOptions,
    departureDateOptions,
    extensionOptions,
    isProductWithSetDates,
    isSummerCamp,
  }: FormSchemaParameters,
) => {
  const agencySchema = yup
    .mixed()
    .oneOf(agencies.map(({ value }) => value))
    .required();
  const fistNameSchema = yup
    .string()
    .required(t('productQuotationForm.coordinates.firstName'))
    .max(20, t('productQuotationForm.coordinates.firstName'));
  const lastNameSchema = yup
    .string()
    .required(t('productQuotationForm.coordinates.lastName'))
    .max(30, t('productQuotationForm.coordinates.lastName'));
  return yup.object().shape({
    adultsNumber: yup.string().required(),
    comment: yup.string(),
    departureCity: yup
      .mixed()
      .oneOf(departureCityOptions.map((c) => c.value))
      .required(),
    departureDate: !isProductWithSetDates
      ? yup
          .object()
          .shape({
            from: yup.date(),
            to: yup.date().nullable(),
          })
          .required()
      : yup
          .mixed()
          .oneOf(departureDateOptions.map((d) => d.value))
          .required(),
    ...(extensionOptions.length > 0
      ? {
          extension: yup
            .mixed()
            .oneOf(extensionOptions.map((e) => e.value))
            .required(),
        }
      : null),
    ...(isSummerCamp
      ? null
      : {
          kidsNumber: yup.string().required(),
        }),
    optinEmail: yup.boolean(),
    traveler: yup.object().shape({
      ...(agencies.length
        ? {
            agency: agencySchema,
          }
        : null),
      civility: yup
        .mixed()
        .oneOf(civilityOptions.map((c) => c.value))
        .required(),
      email: yup
        .string()
        .email(t('productQuotationForm.coordinates.email'))
        .required(t('productQuotationForm.coordinates.email')),
      gmNumber: yup.string().matches(GM_NUMBER_PATTERN),
      firstName: fistNameSchema,
      lastName: lastNameSchema,
      phone: yup
        .string()
        .required(t('productQuotationForm.coordinates.phoneNumber'))
        .matches(/^(\+[1-9]|0)?\d{1,14}$/, {
          excludeEmptyString: true,
          message: t('productQuotationForm.coordinates.phoneNumber'),
        }),
    }),
  });
};
// Submit form
export type FormStatusLink = {
  label: string;
  url: string;
};
export type FormStatus = {
  link: FormStatusLink;
  text: string;
  title: string;
};
export type FormProduct = {
  image: string;
  productId: string;
  productLabel: string;
  productRangeLabel: string;
  rangeId: string;
  seasonName: string;
  minAuthorizedAge: {
    formattedValue: string;
    value: number;
  };
  type: string;
};
type FormSubmitParameters = {
  actions: {
    setSubmitting: (arg0: boolean) => void;
    setStatus: (arg0: { state: string }) => void;
  };
  appContext: AppContext;
  isSummerCamp: boolean;
  newsletter?: {
    id: string;
    description: string;
  };
  product: FormProduct;
  quotation: {
    error: FormStatus;
  };
  redirectUris: {
    success: string | null;
  };
  router: any;
};
type Response = {
  productRequest: {
    statusCode: number;
  };
};
export const FORM_STATUS = {
  NEW: 'new',
  SUCCESS: 'success',
  ERROR: 'error',
};

const getFormattedDepartureDate = (date: FormDepartureDate): string => {
  return typeof date === 'string' ? date : momentToApiDate(moment(date.from || Date.now()));
};

const KIDS_ATTENDEES_TYPE = 'CHILDREN';

const getAttendeesQuery = (attendees, type) => {
  return attendees.reduce((acc, attendee) => {
    return `${acc}${getAttendeeQuery(attendee, type)}`;
  }, '');
};

const formatDate = (date: timestamp | Date) => momentToApiDate(moment(date));

const getAttendeeQuery = (attendee, type) => {
  const { birthdate } = attendee;
  return `{
    birthdate: "${birthdate ? formatDate(birthdate) : ''}"
    type: "${type}"
  }`;
};

export const getProductQuotationFormMutation = (values: FormValues, product: FormProduct) => {
  const {
    adultsNumber,
    kids = [],
    kidsNumber = 0,
    comment,
    departureCity,
    departureDate,
    extension = '',
    optinEmail,
    traveler,
  } = values;
  const { productId, productLabel, productRangeLabel } = product;
  const { civility, email, firstName, lastName, phone, agency, gmNumber = '' } = traveler;
  const formattedDepartureDate = getFormattedDepartureDate(departureDate);
  return `mutation {
    productRequest(
      adultsCount: ${parseInt(adultsNumber, 10)}
      attendeesBirthdates: [${getAttendeesQuery(kids, KIDS_ATTENDEES_TYPE)}]
      childrenCount: ${parseInt(kidsNumber, 10)}
      contact: {
        civility: "${civility}"
        firstName: "${firstName}"
        lastName: "${lastName}"
        email: "${email}"
        mobile: "${phone}"
        agency: "${agency}"
        gmNumber: "${gmNumber}"
      }
      customerComment: ${graphqlifyString(comment)}
      departureCity: "${departureCity || NO_VALUE}"
      departureDate: "${formattedDepartureDate}"
      extensionName: "${extension}"
      productId: "${productId}"
      productLabel: "${productLabel}"
      productRangeLabel: "${productRangeLabel || capitalizeFromUppercase(product.type)}"
      optinEmail: ${optinEmail.toString()}
    ) {
      statusCode
    }
  }`;
};
export const getNewsletterSubscriptionMutation = (values: FormValues, newsletterId: string) => {
  const {
    traveler: { civility, email, firstName, lastName },
  } = values;
  return `mutation {
    subscription (
      civility: "${civility}"
      email: "${email}"
      firstName: "${firstName}"
      isSubscription: true
      lastName: "${lastName}"
      newsletterSubscriptions : ["${newsletterId}"]
      unicodeFirstName: "${firstName}"
      unicodeLastName: "${lastName}"
    ) {
      id
      neolaneId
    }
  }`;
};
export const handleSubmit = async (values: FormValues, params: FormSubmitParameters) => {
  const { actions, appContext, product, quotation, redirectUris } = params;
  actions.setSubmitting(true);
  const productQuotationFormMutation = getProductQuotationFormMutation(values, product);

  try {
    // Post request
    const { productRequest }: Response = await graphQLClient.runNoCacheQuery(
      productQuotationFormMutation,
      appContext,
      {
        requestName: WIDGET_QUOTATION_FORM_SUBMIT,
      },
    );

    if (productRequest.statusCode === 500) {
      actions.setStatus({
        state: FORM_STATUS.ERROR,
        ...quotation.error,
      });
    } else {
      const { router } = params;
      const queryParams = omit(router.query, ['dynamicPage']);
      const hasNoTransport = values.departureCity === '' || values.departureCity === 'NO';
      const url = new URI(redirectUris.success)
        .query({
          ...queryParams,
          productId: params.product.productId,
          departureDate: getFormattedDepartureDate(values.departureDate),
          adults: parseInt(values.adultsNumber, 10),
          children: parseInt(values.kidsNumber, 10),
          transport: hasNoTransport ? 'without transport' : values.departureCity,
        })
        .toString();
      router.push(url);
    }
  } catch (error) {
    console.log(error);
    actions.setStatus({
      state: FORM_STATUS.ERROR,
      ...quotation.error,
    });
  } finally {
    actions.setSubmitting(false);
  }
};
export const getFormUser = (user: UserInfo): Partial<FormTraveler> => {
  const {
    civility = '',
    email = '',
    firstName = '',
    GMNumber = '',
    lastName = '',
    mobilePhoneNumber = '',
    isProspect = false,
  } = user;
  return {
    civility,
    email,
    firstName,
    ...(!isProspect
      ? {
          gmNumber: GMNumber,
        }
      : {}),
    lastName,
    phone: mobilePhoneNumber,
  };
};
export const getKidFutureBithdateValidator = (
  minAuthorizedAgeInMonths?: number,
  maxAuthorizedAgeInMonths?: number,
): yup =>
  yup
    .number()
    .transform((value) => (Number.isNaN(value) ? undefined : value))
    .test('isOldEnough', function (value: number) {
      //errorMessage, function (value: number) {
      const { context } = this.options;

      if (value && context?.values.departureDate) {
        const departureDate = moment(context?.values.departureDate).startOf('day');
        const kidBirthdate = moment(value).startOf('day');
        const validBirthdate = kidBirthdate.isBefore(departureDate);

        if (minAuthorizedAgeInMonths && maxAuthorizedAgeInMonths) {
          const ageInMonths = departureDate.diff(kidBirthdate, 'months');
          console.log(ageInMonths);
          return (
            validBirthdate &&
            ageInMonths >= minAuthorizedAgeInMonths &&
            ageInMonths <= maxAuthorizedAgeInMonths
          );
        }

        return validBirthdate;
      }

      return false;
    })
    .nullable()
    .required();
